<template>
    <div>
        <Head title="Замовлення товарів"/>
        <h1 class="mb-8 text-3xl font-bold">Замовлення товарів</h1>

        <!-- Filters -->
        <div class="flex flex-wrap gap-4 items-end mb-6">
            <div>
                <label class="block text-sm font-medium text-gray-600 mb-1">Пошук</label>
                <input
                    v-model="filters.search"
                    type="text"
                    placeholder="Імʼя або телефон..."
                    class="border-gray-300 rounded-md shadow-sm text-sm px-3 py-2"
                    @input="applyFilters"
                />
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-600 mb-1">Статус оплати</label>
                <select
                    v-model="filters.is_payed"
                    class="border-gray-300 rounded-md shadow-sm text-sm px-3 py-2"
                    @change="applyFilters"
                >
                    <option value="">Усі</option>
                    <option value="1">Оплачені</option>
                    <option value="0">Не оплачені</option>
                </select>
            </div>
        </div>

        <!-- Totals -->
        <div class="flex gap-6 mb-6">
            <div class="bg-white rounded-md shadow px-6 py-4">
                <p class="text-xs text-gray-500 uppercase tracking-wide">Оплачено</p>
                <p class="text-2xl font-bold text-green-600 mt-1">{{ totalPaid.toFixed(2) }} грн</p>
            </div>
            <div class="bg-white rounded-md shadow px-6 py-4">
                <p class="text-xs text-gray-500 uppercase tracking-wide">Не оплачено</p>
                <p class="text-2xl font-bold text-red-500 mt-1">{{ totalUnpaid.toFixed(2) }} грн</p>
            </div>
        </div>

        <!-- Table -->
        <div class="bg-white rounded-md shadow overflow-x-auto">
            <table class="w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                    <th class="pb-4 pt-6 px-6">#</th>
                    <th class="pb-4 pt-6 px-6">Товар</th>
                    <th class="pb-4 pt-6 px-6">Покупець</th>
                    <th class="pb-4 pt-6 px-6">Телефон</th>
                    <th class="pb-4 pt-6 px-6">Адреса доставки</th>
                    <th class="pb-4 pt-6 px-6">Кількість</th>
                    <th class="pb-4 pt-6 px-6">Сума</th>
                    <th class="pb-4 pt-6 px-6">Оплата</th>
                    <th class="pb-4 pt-6 px-6">Відгрузка</th>
                    <th class="pb-4 pt-6 px-6">Дата</th>
                </tr>
                <tr
                    v-for="order in orders.data"
                    :key="order.id"
                    :class="rowClass(order)"
                >
                    <td class="border-t px-6 py-4 text-gray-500 text-sm">{{ order.id }}</td>
                    <td class="border-t px-6 py-4">{{ order.product }}</td>
                    <td class="border-t px-6 py-4">{{ order.name }}</td>
                    <td class="border-t px-6 py-4">{{ order.phone }}</td>
                    <td class="border-t px-6 py-4 text-sm text-gray-600 max-w-xs">{{ order.delivery_address || '—' }}</td>
                    <td class="border-t px-6 py-4">{{ order.quantity }}</td>
                    <td class="border-t px-6 py-4 font-medium">{{ order.total_price }} грн</td>
                    <td class="border-t px-6 py-4">
                        <span
                            class="px-2 py-1 rounded-full text-xs font-semibold"
                            :class="order.is_payed
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-600'"
                        >
                            {{ order.is_payed ? 'Оплачено' : 'Не оплачено' }}
                        </span>
                    </td>
                    <td class="border-t px-6 py-4">
                        <select
                            :value="order.is_shipped ? '1' : '0'"
                            @change="setShipped(order, $event.target.value)"
                            class="appearance-none text-xs font-semibold rounded-full px-3 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 focus:outline-none"
                            :class="order.is_shipped
                                ? 'bg-teal-100 text-teal-800 focus:ring-teal-400'
                                : 'bg-amber-100 text-amber-800 focus:ring-amber-400'"
                        >
                            <option value="0">Не відвантажено</option>
                            <option value="1">Відвантажено</option>
                        </select>
                    </td>
                    <td class="border-t px-6 py-4 text-gray-500 text-sm">{{ order.created_at }}</td>
                </tr>
                <tr v-if="orders.data.length === 0">
                    <td class="px-6 py-4 border-t" colspan="10">Замовлень немає</td>
                </tr>
            </table>
        </div>
        <pagination class="mt-6" :links="orders.links"/>
    </div>
</template>

<script>
import {Head} from '@inertiajs/inertia-vue3'
import {Inertia} from '@inertiajs/inertia'
import {throttle} from 'lodash'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'

export default {
    components: {Head, Pagination},
    layout: Layout,
    props: {
        orders:      {type: Object, required: true},
        filters:     {type: Object, default: () => ({})},
        totalPaid:   {type: Number, default: 0},
        totalUnpaid: {type: Number, default: 0},
    },
    data() {
        return {
            filters: {
                search:   this.filters.search   ?? '',
                is_payed: this.filters.is_payed ?? '',
            },
        }
    },
    methods: {
        applyFilters: throttle(function () {
            Inertia.get(
                this.$route('product-orders.index'),
                this.filters,
                {preserveState: true, replace: true}
            )
        }, 300),
        rowClass(order) {
            if (order.is_payed && order.is_shipped)  return 'bg-green-50 hover:bg-green-100'
            if (order.is_payed && !order.is_shipped) return 'bg-amber-50 hover:bg-amber-100'
            if (!order.is_payed && order.is_shipped) return 'bg-gray-50 hover:bg-gray-100'
            return 'hover:bg-red-50'
        },
        setShipped(order, value) {
            const isShipped = value === '1'
            if (isShipped === order.is_shipped) return
            Inertia.patch(
                this.$route('product-orders.toggle-shipped', order.id),
                {},
                {preserveState: true, preserveScroll: true}
            )
        },
    },
}
</script>
